<template>
    <div>
        <ErrorPage v-if="errorState"/>
        <NetworkStatus
            :data="data"
            :isOpenedSidebar="isOpenedSidebar"
        />
        <div
            v-show="!errorState"
            class="widget"
            id="fullScreen"
            ref="sidebar"
            :class="{
                'openedSidebar': getSidebarStatus() && hasSidebarWidget,
                'hasTitle': widget === 'program',
                'full-screen-background-color': widget,
                'alternative-layout': isAlternativeMode
            }"
        >
            <AlternativeTabs
                v-if="isAlternativeMode && hasAlternativeMenu"
                :data="data"
                :backButton="showBackButton"
                :goBack="setPrevState"
                :isStandalone="widget !== 'program'"
            />
            <div
                v-else-if="hasSidebarWidget"
                id="sidebar"
                class="sidebar"
                :style="{width: widthSidebar + 'px'}"
                v-click-outside="closeSidebar"
            >
                <Sidebar
                    :data="data"
                    :backButton="showBackButton"
                    :mainMenuButton="showMainMenuButton ? goToMainMenu : null"
                    :backViewButton="showBackViewButton ? setPrevSidebar : null"
                    :currentWidgetTitle="currentWidgetTitle"
                    :goBack="setPrevState"
                    :isStandalone="widget !== 'program'"
                    :noSubTitle="widgetsWithNoSubtitles.includes(widget)"
                    :isOpenedSidebar="isOpenedSidebar"
                    :widthSidebar="widthSidebar"
                    :isOpenedProgramInfo="isOpenedProgramInfo"
                    :language="languageSet"
                    @toggle-sidebar="toggleSidebarAction"
                />

                <InformationCard
                    v-if="hasInformationCard"
                    :programName="data.program.name"
                    :isOpenedProgramInfo="isOpenedProgramInfo"
                    :isOpenedSidebar="isOpenedSidebar"
                />
            </div>
            <div
                id="mega"
                ref="mega"
                class="content-widget"
                :style="{
                    'margin-left': isOpenedSidebar && hasSidebarWidget ? widthSidebar + 'px' : 0,
                    'height': screenHeight
                }"
                :class="{
                    'alternative': isAlternativeMode,
                    'extended': isAlternativeMode && isOpenedNavbar
                }"
            >
                <AllWidgets
                    v-if="coreLoaded"
                    :data="data"
                    :loadedWidgets="loadedWidgets"
                    :defaultWidgetTitle="defaultWidgetTitle"
                    :defaultState="defaultState"
                    :mode="mode"
                    :widgetResolutions="widgetResolutions"
                    :isOpenedSidebar="isOpenedSidebar"
                    :isMobile="isMobileScreen"
                />
            </div>
            <div class="flash-message">
                <div :class="{
                        'is-visible': isDisplayed,
                        'online' : isOnline,
                        'offline': !isOnline
                    }"
                >
                    {{ flashMessage }}
                </div>
            </div>
        </div>
    </div>
        
</template>

<script>
    import _, { debounce } from 'lodash';
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';

    import AlternativeTabs from '@/layout/alternative/AlternativeTabs';
    import Sidebar from '@/layout/sidebar/Sidebar.vue';

    import AllWidgets from '@/components/AllWidgets';
    import ErrorPage from '@/components/ErrorPage';
    import NetworkStatus from '@/components/NetworkStatus';
    import InformationCard from "@/components/programInformation/InformationCard";

    export default {
        name: 'MegaWidget',
        components: {
            AllWidgets,
            AlternativeTabs,
            InformationCard,
            ErrorPage,
            Sidebar,
            NetworkStatus
        },
        metaInfo: () => ({
            meta: [
                {
                    property: 'og:title',
                    content: this.metaTitle
                },
                {
                    property: 'og:type',
                    content: 'website'
                },
                {
                    property: 'og:image',
                    content: this.metaImage
                },
                {
                    property: 'og:image:width',
                    content:  '400'
                },
                {
                    property: 'og:image:height',
                    content:  '300'
                },
                {
                    property: 'og:description',
                    content: this.metaDescription
                }
            ]
        }),
        props: [
            'widget',
            'mode'
        ],
        data: () => ({
            isIE: false,
            metaTitle: null,
            metaImage: null,
            isOutSideSidebar: false,
            metaDescription: "",
            errorState: false,
            data: {},
            lastSidebarType: null,
            currentSidebarType: null,
            defaultSidebarType: null,
            loadedWidgets: [],
            storedPayload: [],
            state: [],
            defaultState: null,
            isOpenedSidebar: true,
            userSidebarChoice: false,
            showBackButton: false,
            widgetResolutions: {},
            skippedWidgets: [
                'sunshine',
                'configurator'
            ],
            widgetsWithSpecificSidebar: [
                'cutaway',
                'plot-grid',
                'plan',
                '3d-plan',
                'map',
                'plot',
                'documentation',
                'general',
                'models',
                'conf-models',
                'configurable-tour'
            ],
            allPlotWidgets: [
                '2d-plan',
                'configurable-tour',
                'virtual-tour',
                '3d-plan',
                'drone-floor-view'
            ],
            availableBackViewButton: [
                'program',
                'digital-model',
                'general',
                'models',
                'conf-models',
            ],
            ignoredSidebarType: [
                'configurable-tour'
            ],
            availableHotspotSidebarType: [
                'general',
                'perspective',
                'plot'
            ],
            currentWidgetTitle: null,
            defaultWidgetTitle: null,
            flashMessage: null,
            isDisplayed: false,
            isMobileScreen: false,
            screenHeight: 0,
            isOpenedProgramInfo: false,
            coreLoaded: false,
            savedWidgets: [
                'cutaway',
                'plot-grid'
            ],
            isMainMenu: false,
            skipUpdateSidebarType: false,
            widgetsWithNoSubtitles: [ 
                'cutaway',
                'plot'
            ],
            isExtended: true,
            widgetsWithNoInformationCard: ['plot-grid']
        }),
        computed: {
            ...mapGetters({
                availableTrackings: 'availableTrackings',
                isAlternativeMode: 'isAlternativeMode',
                hasSidebar: 'hasSidebar',
                isReviewUse: 'isReviewUse',
                isLocalstorageAvailable: 'isLocalstorageAvailable',
                isOnline: 'isOnline',
                protocol: 'protocol',
                logos: 'logos',
                disableCloseSidebar: 'disableCloseSidebar',
                isFrontless: 'isFrontless',
                isOpenedNavbar: 'isOpenedNavbar'
            }),
            widthSidebar () {
                switch (this.$vuetify.breakpoint.name) {
                    // case 'xs':
                    // case 'sm':
                    // case 'md':
                    //     return 235;

                    // case 'md':
                    // case 'lg':
                    // case 'xl':
                    //     return 344;
                    default:
                        return 340;
                }
            },
            languageKey() {
                return `language-${this.$route.query.key}`;
            },
            languageSet() {
                if (this.$route.query.language)
                    return this.$route.query.language;
                else if (this.isLocalstorageAvailable)
                    if (localStorage.getItem(this.languageKey))
                        return localStorage.getItem(this.languageKey);
                return this.$i18n.locale;
            },
            isProgramWidget() {
                return window.location.href.indexOf("program") > -1;
            },
            showMainMenuButton () {
                return !this.isMainMenu &&
                        !this.isIgnoredSidebarType &&
                        (this.lastSidebarIsDefaultSidebar || this.currentSidebarIsSpecificSidebar) &&
                        !this.currentSidebarIsDefaultSidebar &&
                        this.isAvailableMainMenuWithHotspots &&
                        this.isAvailableBackViewButton;
            },
            showBackViewButton () {
                return this.currentSidebarType !== this.lastSidebarType &&
                        !this.isIgnoredSidebarType &&
                        this.lastSidebarIsSpecificSidebar &&
                        (this.currentSidebarIsDefaultSidebar || this.isAvailableBackViewButtonWithHotspots) &&
                        this.isAvailableBackViewButton;
            },
            currentSidebarIsDefaultSidebar () {
                return this.data.type === this.defaultSidebarType;
            },
            lastSidebarIsDefaultSidebar () {
                return this.lastSidebarType === this.defaultSidebarType;
            },
            isIgnoredSidebarType() {
                return this.ignoredSidebarType.includes(this.currentSidebarType);
            },
            isAvailableBackViewButton() {
                return this.availableBackViewButton.includes(this.widget);
            },
            isAvailableBackViewButtonWithHotspots() {
                return this.data.activeHotspot &&  (this.lastSidebarType === 'cutaway' || this.lastSidebarType === 'plot');
            },
            lastSidebarIsSpecificSidebar() {
                return this.widgetsWithSpecificSidebar.includes(this.lastSidebarType);
            },
            currentSidebarIsSpecificSidebar() {
                return this.widgetsWithSpecificSidebar.includes(this.currentSidebarType);
            },
            isAvailableMainMenuWithHotspots() {
                return !this.data.activeHotspot || this.data.type === 'cutaway' || this.data.type === 'plot';
            },
            hasAlternativeMenu() {
                switch (this.widget) {
                    /**
                     * allow these widget to use alternative menu
                     */
                    case 'carousel':
                    case 'carousel-lot':
                    case 'drone-view':
                    case 'perspective':
                    case 'district-video':
                    case 'animation':
                        return false;
                    case 'virtual-tour':
                        if (this.data.items && this.data.items[0].submenu)
                            return true;
                        return false;
                    default:
                        return true;
                }
            },
            hasSidebarWidget() {
                if (!this.hasSidebar || this.isAlternativeMode || this.isFrontless) 
                    return false;
                switch (this.widget) {
                    /**
                     * delete sidebar for these widget
                     */
                    case 'carousel':
                    case 'carousel-lot':
                    case 'drone-view':
                    case 'perspective':
                    case 'district-video':
                    case 'animation':
                        return false;

                    /**
                     * Delete sidebar for virtual-tour without alternate model (submenu)
                     */
                    case 'virtual-tour':
                        if (this.data.items && this.data.items[0].submenu)
                            return true;
                        return false;

                    /**
                     * delete sidebar only on ground-plan when not in hotspot
                     */
                    case 'digital-model':
                        return this.mode !== 'ground-plan' || this.showBackButton;

                    default:
                        return true;
                }
            },
            promoterLogo() {
                return this.logos && 'promoter' in this.logos ? this.logos['promoter'] : null;
            },
            programLogo() {
                return this.logos && 'program' in this.logos ? this.logos['program'] : null;
            },
            hasInformationCard(){
                if((this.promoterLogo || this.programLogo))
                    return  !this.isOpenedSidebar && this.widgetsWithNoInformationCard.includes(this.data.type) ? false : true;
                else 
                    return false;
            }
        },
        created() {
            this.setIsLoading(true);
            this.setStartedWidget(this.$props.widget);
            this.setRoute(this.$route);

            this.checkGPDRConsent();

            // Save in store : language used by user
            this.setLanguage(this.languageSet);
            this.$i18n.locale = this.languageSet;

            let vm = this;
            const WIDGET_CORE = process.env.WIDGET_CORE;
            const CORE_ENV = WIDGET_CORE.ENV;
            const CORE_SCRIPTS = WIDGET_CORE.SCRIPTS;

            let coreUtilsUrl = `${this.protocol}${CORE_SCRIPTS.CORE}?v=${CORE_ENV.VERSION}`;
            this.injectScript(coreUtilsUrl).then(() => {
                Habiteo.setConfig(CORE_ENV);
                let coreWidgetsUrl = `${this.protocol}${CORE_SCRIPTS.WIDGETS}?v=${CORE_ENV.VERSION}`;
                this.injectScript(coreWidgetsUrl).then(() => {
                    let coreApiUrl = `${this.protocol}${CORE_SCRIPTS.API}?v=${CORE_ENV.VERSION}`;
                    this.injectScript(coreApiUrl).then(() => {
                        vm.coreLoaded = true;
                    }, () => {
                        console.error('bienici.api not loaded');
                    });
                }, () => {
                    console.error('Habiteo.widgets not loaded');
                });
            }, () => {
                console.error('habiteo.core not loaded');
            });
        },
        mounted() {
            this.setScreenSize();
            window.addEventListener('resize', this.setScreenSize);

            this.isMobile();
            window.addEventListener('resize', this.isMobile);

            this.isIE = window.navigator.msPointerEnabled;
            window.addEventListener('resize', debounce(this.onResize, 250));

            if (this.widget !== 'program')
                this.updateLoadedWidgets({
                    id: this.widget,
                    link: null,
                    active: true,
                    payload: null
                });

            if (this.isAlternativeMode)
                this.isOpenedSidebar = false;

            EventBus.$on('getBack', () => {
                this.errorState = false;
                this.setPrevState();
            });

            EventBus.$on('updateLoadedWidgets', obj => {
                let isSkippedWidgets = false;
                this.skippedWidgets.forEach(function(widget){
                    if (widget === obj.widgetType) {
                        isSkippedWidgets = true;
                        return;
                    }
                });

                if (!isSkippedWidgets) {
                    // If a resolution exist for current widget to load. If not, it will be added as 720p
                    if (!this.widgetResolutions[obj.widgetType])
                        this.widgetResolutions[obj.widgetType] = '720p';
                    else {
                        if (!this.isIE) {
                            const currentLink = new URL(obj.link);
                            // Add resolution parameter if a resolution has been set previously
                            if (this.widgetResolutions[obj.widgetType] !== '720p')
                                currentLink.searchParams.set('resolution', this.widgetResolutions[obj.widgetType]);
                            else
                                currentLink.searchParams.delete('resolution');

                            // Prevent the addition of '=' due to URL class for parameter with no value
                            if (currentLink.href.includes('offline'))
                                obj.link = currentLink.href.replace('&offline=', '') + '&offline';
                            else
                                obj.link = currentLink.href;
                        }
                    }
                    EventBus.$emit('updateNavigationBarResolution', this.widgetResolutions[obj.widgetType]);
                }
                this.updateLoadedWidgets(obj);
            });

            EventBus.$on('setErrorState', (value, msg) => {
                if (value)
                    console.error(msg);
                this.errorState = value;
            });

            EventBus.$on('setDefaultState', data => {
                this.defaultState = data;
            });

            EventBus.$on('setSidebarData', data => {
                
                this.data = data;
            });

            EventBus.$on('updateSidebar', (data, save = true) => {
                if (save)
                    this.saveState();

                this.isMainMenu = false;
                this.savePayload(data);

                this.data = Object.assign({}, this.data, data);
                if (this.state.length > 0)
                    this.showBackButton = true;

                this.data.showHotspotName = !this.data.activeHotspot ? false : true;
            });

            EventBus.$on('saveSidebar', () => {
                this.saveState();
            });

            // Save widget payload
            EventBus.$on('savePayload', data => {
                this.savePayload(data);
            });

            // Event to update resolution
            EventBus.$on('updateResolution', resolution => {
                const currentLoadedWidget = this.loadedWidgets.filter(widget => {
                    return widget.active === true;
                })
                const widgetId = currentLoadedWidget[0].id;
                const widgetType = currentLoadedWidget[0].widgetType;
                const currentLink = new URL(currentLoadedWidget[0].link);

                if (resolution !== '720p')
                    currentLink.searchParams.set('resolution', resolution);
                else
                    currentLink.searchParams.delete('resolution');
                // Save the resolution set to this widget
                this.widgetResolutions[widgetType] = resolution;
                this.updateLoadedWidgets({
                    id: widgetId,
                    link: currentLink.href,
                    active: true,
                    widgetType: widgetType
                });
            });

            // Update hotspots visibility matching filters
            EventBus.$on('filterHotspots', (filters, callback) => {
                let currentHotspot = {},
                disabledAvailabilites = [],
                disabledTypologies = [],
                enabledTags = [],
                isTypologyDisabled = false,
                isFavoriteDisabled = false,
                hasTagSelected = false,
                isAvailabilityDisabled = false,
                isPriceInInterval = false,
                isAreaInInterval = false,
                currentPrice = null,
                currentArea = null;

                // Get disabled availabilities from data.filters.availabilites object
                Object.keys(filters.availabilities).forEach(key => {
                    if (!filters.availabilities[key].active)
                        disabledAvailabilites.push(key);
                });

                // Get all enabled tags
                filters.tags.forEach(tag => {
                    if (tag.active)
                        enabledTags.push(tag.id);
                });

                // Get disabled typologies from data.filters.typologies object
                Object.keys(filters.typologies).forEach(key => {
                    if (!filters.typologies[key].active)
                        disabledTypologies.push(key);
                });

                // Loop on hotspots for each floor to loop on hotspot details to get values used for sorting
                this.data.floors.forEach(floor => {
                    floor.hotspots.forEach(hotspot => {
                        currentHotspot = {};
                        currentHotspot.name = hotspot.name;

                        // Search plot filters
                        if (filters.searchValue)
                            hotspot.visible = filters.searchValue === currentHotspot.name;
                        else {
                            hotspot.details.forEach(detail => {
                                switch (detail.id) {
                                    case 'category':
                                    case 'availability':
                                        currentHotspot[detail.id] = detail.value;
                                        break;

                                    case 'totalPrice':
                                        let price = detail.value.replace(/\s/g, '');
                                        if (this.languageSet === 'en') {
                                            // Remove ',' thousand separator
                                            price = price.replaceAll(',', '');
                                        } else {
                                            // Remove '.' thousand separator
                                            price = price.replaceAll('.', '');
                                        }
                                        currentHotspot[detail.id] = parseFloat(price.match(/[0-9]+/g));
                                        break;

                                    case 'surface':
                                        let surface = detail.value.replace(/\s/g, '');
                                        if (this.languageSet === 'en') {
                                            // Remove ',' thousand separator
                                            surface = surface.replaceAll(',', '');
                                        } else {
                                            // Remove '.' thousand separator
                                            surface = surface.replaceAll('.', '');
                                        }
                                        currentHotspot[detail.id] = parseFloat(surface.match(/[0-9]+/g));
                                        break;

                                    case 'tags':
                                        if (detail.tags != null) {
                                            currentHotspot.tags = [];
                                            Object.keys(detail.tags).forEach(tag => {
                                                currentHotspot.tags.push(tag);
                                            });
                                        } else
                                            currentHotspot.tags = null;
                                        break;

                                    default:
                                        break;
                                }
                            });

                            // Plot typology filter
                            isTypologyDisabled = disabledTypologies.length > 0 ? (disabledTypologies.length === Object.entries(filters.typologies).length ? false : disabledTypologies.includes(currentHotspot['category'])) : false;

                            // Plot availability filter
                            isAvailabilityDisabled = disabledAvailabilites.length > 0 ? (disabledAvailabilites.length === Object.entries(filters.availabilities).length ? false : disabledAvailabilites.includes(currentHotspot.availability)) : false;

                            // Plot price filter
                            currentPrice = currentHotspot.price ? currentHotspot.price : 0;
                            isPriceInInterval = filters.userPriceInterval ? ((currentPrice >= filters.userPriceInterval.min && currentPrice <= filters.userPriceInterval.max)) : true;

                            // Plot area filter
                            currentArea = currentHotspot.surface;
                            isAreaInInterval = filters.userAreaInterval ? ((currentArea >= filters.userAreaInterval.min && currentArea <= filters.userAreaInterval.max) || currentArea === null) : true;

                            // Favorite filter
                            isFavoriteDisabled = filters.enableFavorite ? (!filters.enableFavorite.includes(currentHotspot.name) ? true : false) : false;

                            // Tag filter
                            if (enabledTags.length > 0)
                                if (currentHotspot.tags)
                                    hasTagSelected = enabledTags.every(tag => currentHotspot.tags.indexOf(tag) >= 0) ? true : false;
                                else
                                    hasTagSelected = false;
                            else
                                hasTagSelected = true;

                            if (isTypologyDisabled || isAvailabilityDisabled || !isPriceInInterval || isFavoriteDisabled || !hasTagSelected || !isAreaInInterval)
                                hotspot.visible = false;
                            else
                                hotspot.visible = true;
                        }
                    });
                });
                callback(this.data.floors);
            });

            EventBus.$on('closeSidebar', () => {
                if (this.isOpenedSidebar) {
                    this.toggleMenu();
                    this.userSidebarChoice = true;
                }
            });

            EventBus.$on('toggleSidebar', (isOutSideSidebar = false) => {
                this.isOutSideSidebar = isOutSideSidebar;
                this.toggleMenu();
                this.userSidebarChoice = true;
            });

            EventBus.$on('openProgramInfo', () => {
                this.isOpenedProgramInfo = true;
            });

            EventBus.$on('closeProgramInfo', () => {
                this.isOpenedProgramInfo = false;
            });

            // Display flash message on the bottom page
            EventBus.$on('displayFlashMessage', message => {
                this.displayFlashMessage(message);
            });

            // Get saved data for loaded widget
            EventBus.$on('getLoadedWidget', (widgetTypeList, widgetId, callback) => {
                let payloadIndex = this.storedPayload.findIndex(payload => widgetTypeList.includes(payload.type) && payload.id == widgetId);
                let payload = null;
                if (payloadIndex >= 0)
                    payload = this.storedPayload[payloadIndex];

                callback(payload);
            });

            // Load external URL in iframe
            EventBus.$on('loadIframeWidget', (widgetId, url) => {
                this.updateLoadedWidgets({
                    'id': widgetId,
                    'link': url,
                    'active': true,
                    'widgetType': widgetId
                });
            });

            EventBus.$on('getSidebarSatus', callback => {
                callback(this.isOpenedSidebar);
            });

            EventBus.$on('updateDefaultSidebar', () => {
                if (this.currentSidebarType === 'program'  )
                    this.defaultSidebarType = this.currentSidebarType;
            });

            EventBus.$on('convertAreaUnit', unit => {
                this.data.areaUnit = unit;
            });
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.isMobile);
        },
        methods: {
            ...mapActions([
                'checkGPDRConsent',
                'setLanguage',
                'setRoute',
                'setStartedWidget',
                'setIsLoading'
            ]),
            redirectWithLanguage() {
                let redirectUrl = new URL(window.location.href);
                redirectUrl.searchParams.set('language', this.languageSet);
                window.location.href = redirectUrl.href;
            },
            injectScript(file) {
                return new Promise((resolve, reject) => {
                    var script = document.createElement('script');
                    script.src = file;
                    script.type ='text/javascript';
                    script.defer = true;
                    script.setAttribute('crossorigin', 'anonymous');
                    document.getElementsByTagName('head').item(0).appendChild(script);
                    script.onload = () => {
                        resolve();
                    }
                    script.onerror = () => {
                        reject();
                    }
                })
            },
            setScreenSize() {
                this.screenHeight = window.innerHeight + 'px';
            },
            getSidebarStatus() {
                if (this.isAlternativeMode) {
                    const currentLoadedWidget = this.loadedWidgets.filter(widget => {
                        return widget.active === true;
                    });
                    if (currentLoadedWidget[0] === undefined)
                        return false;

                    const widgetId = currentLoadedWidget[0].id;
                    switch (widgetId) {
                        case 'cutaway':
                        case 'plot-grid':
                        case 'map-view':
                            break;

                        default:
                            this.isOpenedSidebar = false;
                            break;
                    }
                }
                return this.isOpenedSidebar;
            },
            updateLoadedWidgets(widget) {
                this.loadedWidgets.filter(el => el.active).forEach(el => el.active = false);
                let loadedWidget = this.loadedWidgets.find(loaded => loaded.id === widget.id);
                if (loadedWidget) {
                    loadedWidget.active = widget.active;
                    loadedWidget.link = widget.link;
                    loadedWidget.widgetType = widget.widgetType;
                    loadedWidget.type = widget.type || null;
                } else
                    this.loadedWidgets.push(widget);

                if (!this.widgetsWithSpecificSidebar.includes(widget.type))
                    this.lastSidebarType = this.defaultSidebarType;
            },
            goToMainMenu() {
                this.data.type = this.defaultSidebarType;
                this.isMainMenu = true;
                this.data.isMainMenu = true;
                this.data.showHotspotName = !this.availableHotspotSidebarType.includes(this.currentSidebarType);
            },
            setPrevSidebar() {
                this.data.type = this.lastSidebarType;
                this.isMainMenu = false;
                this.data.isMainMenu = false;
                this.data.showHotspotName = false;
            },
            setLegacySidebar(sidebarType) {
                if (this.currentSidebarType) {
                    if (!this.skipUpdateSidebarType)
                        if (!this.state.length && !this.isMainMenu)
                            this.lastSidebarType = sidebarType;
                        else
                            this.lastSidebarType = this.currentSidebarType;
                    this.currentSidebarType = sidebarType;
                    if (('activeHotspot' in this.data && this.data.activeHotspot) && this.currentSidebarType !== 'general') {
                        if (this.lastSidebarType === 'cutaway')
                            this.defaultSidebarType = 'general';
                        else
                            this.defaultSidebarType = this.lastSidebarType;
                    }
                    else if (this.isProgramWidget) {
                        this.defaultSidebarType = 'program';
                        if (this.lastSidebarType === 'general')
                            this.lastSidebarType = this.defaultSidebarType;
                    }
                } else if (this.defaultSidebarType === null) {
                    // initial set legacy sidebar properties
                    this.defaultSidebarType = sidebarType
                    this.lastSidebarType = sidebarType;
                    this.currentSidebarType = sidebarType;
                }

                const currentLoadedWidget = this.loadedWidgets.filter(widget => {
                    return widget.active === true;
                })

                if (this.allPlotWidgets.includes(currentLoadedWidget[0].id))
                    if (this.isProgramWidget)
                        this.data.widgetList.filter(el => el.active).forEach(el => el.active = false);
                    else if (this.data.levels)
                        this.data.levels.filter(el => el.active).forEach(el => el.active = false);

                if (this.data.activeHotspot && this.data.activeHotspot.type === currentLoadedWidget[0].id)
                    this.data.showHotspotName = this.availableHotspotSidebarType.includes(this.data.type);

                this.skipUpdateSidebarType = false;
            },
            setPrevState() {
                if (this.state.length) {
                    let obj = this.state.pop();
                    this.data = obj.data;
                    if (this.state.length === 0 && !!this.defaultState)
                        this.data.widgetList = this.defaultState.widgetList;
                    this.lastSidebarType = this.data.type;
                    this.skipUpdateSidebarType = true;
                    let previousWidget = obj.loadedWidgets.filter(el => el.active)[0];
                    if(this.isAlternativeMode)
                        this.data.widgetList.filter(el => el.id === previousWidget.id ||   el.id === previousWidget.widgetType).forEach(el => el.active = true);
                    this.updateLoadedWidgets(previousWidget);
                    if (this.state.length > 0)
                        this.showBackButton = true;
                    else
                        this.showBackButton = false;
                }
            },
            saveState() {
                let data = _.cloneDeep({ data: this.data, loadedWidgets: this.loadedWidgets });
                this.state.push(data);
            },
            onResize() {
                if (!this.isAlternativeMode)
                    if ((!this.userSidebarChoice) &&
                        (((window.innerWidth >= 750) && (this.isOpenedSidebar === false)) || ((window.innerWidth < 750) && (this.isOpenedSidebar === true))))
                        this.toggleMenu();
            },
            triggerResize() {
                if (this.isIE) {
                    let evt = window.document.createEvent('UIEvents');
                    evt.initUIEvent('resize', true, false, window, 0);
                    window.dispatchEvent(evt);
                } else
                    window.dispatchEvent(new Event('resize'));
            },
            toggleSidebarAction() {
                this.toggleMenu();
                this.userSidebarChoice = true;
            },
            toggleMenu() {
                let start = null;
                let vm = this;
                function step(timestamp) {
                    if (!start)
                        start = timestamp;
                    let progress = timestamp - start;
                    vm.triggerResize();
                    if (progress < 500)
                        window.requestAnimationFrame(step);
                }
                window.requestAnimationFrame(step);
                this.isOpenedSidebar = !this.isOpenedSidebar;
            },
            savePayload(data) {
                let currentWidgetIndex = this.loadedWidgets.findIndex(widget => widget.widgetType === data.type);
                if (currentWidgetIndex !== -1 && this.savedWidgets.includes(data.type)) {
                    let payloadIndex = this.storedPayload.findIndex(payload => payload.id == data.id && payload.type == data.type);
                    if (payloadIndex >= 0)
                        this.storedPayload[payloadIndex] = data;
                    else
                        this.storedPayload.push(data);
                }
            },
            updateMetaInfo(data) {
                if (this.widget === "plot" || this.widget === "3d-plan" || this.widget === "2d-plan" || this.widget === "virtual-tour") {
                    let details = data.details;
                    let threeD;
                    let threeDImage;

                    if (this.widget === "virtual-tour") {
                        var item = null;
                        if (data.items)
                            item = data.items.filter((el) => el.active).find((item) => item.details);
                        details = item ? item.details : null;
                    }

                    const name = details ? details.find((detail) => detail.id === "name") : null;
                    const category = details ? details.find((detail) => detail.id === "category") : null;
                    if (!!data.items)
                        threeD = data.items.find((item) => item.id === "3d-plan");

                    if (!!threeD)
                        this.metaImage = threeD.preview.replace("-mini","");

                    if (data.program) {
                        if (data.program.name !== null && data.program.city !== null && category && name)
                            this.metaTitle = category.value + " : " + name.value + " / " + data.program.name + " - " + data.program.city;
                        if (this.metaImage !== null && data.program.image !== null)
                            this.metaImage = data.program.image;
                    }
                } else {
                    if (data.program) {
                        if (data.program.name !== null && data.program.city !== null)
                                this.metaTitle = data.program.name + " - " + data.program.city;

                        if (data.program.image !== null)
                            this.metaImage = data.program.image;
                    }
                }

                if (data.program && data.program.description !== null)
                    this.metaDescription = data.program.description;
            },
            displayFlashMessage(message) {
                this.flashMessage = message;
                this.isDisplayed = true;
                setTimeout(() => {
                    this.isDisplayed = false;
                }, 5000);
            },
            isMobile() {
                this.isMobileScreen = window.innerWidth < 750 ? true : false;
            },
            closeSidebar() {
                // If the click is outside the sidebar on a tablet or mobile view, the sidebar will be closed
                if (this.isMobileScreen && this.isOpenedSidebar && !this.isOutSideSidebar && !this.disableCloseSidebar) {
                    this.toggleMenu();
                    this.userSidebarChoice = true;
                }
                this.isOutSideSidebar = false;
            },
        },
        watch: {
            'data.widgetTitle'(val) {
                this.currentWidgetTitle = val;
                if (!this.defaultWidgetTitle)
                    this.defaultWidgetTitle = val;
            },
            'data.type'(val) {
                this.setLegacySidebar(val);
            },
            data(val) {
                this.updateMetaInfo(val);
            },
            isMobileScreen() {
                if (!this.userSidebarChoice) {
                    this.isOpenedSidebar = this.isMobileScreen;
                    this.toggleMenu();
                }
            },
            isOpenedSidebar() {
                EventBus.$emit('updateSidebarStatus', this.isOpenedSidebar);
            }
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/_common.scss";
    @import "@/assets/scss/main.scss";
    @import "@/assets/scss/components/_popup.scss";
    @import "@/assets/scss/components/_sidebar.scss";
    @import "@/assets/scss/components/_btn-play.scss";
    @import "@/assets/scss/components/_flash_message.scss";

    .full-screen-background-color{
        background-color: #fff;
    }
</style>


// WEBPACK FOOTER //
// src/components/MegaWidget.vue